import { defineStore } from 'pinia';
import * as Claims from '@cyber-range/cyber-range-shared-claims';
import { useAuthenticationStore } from './authenticationStore';
import { useEntitlementStore } from './entitlementStore';

export const useAuthorizationStore = defineStore('authorizationStore', {
    getters: {
        canListCourses: () => (resourceIds: string[]) => {
            return useEntitlementStore().hasClaim(Claims.Type.API, Claims.Value.LIST_V1_COURSES, [
                ...resourceIds,
                Claims.Scope.UNDEFINED,
                Claims.Scope.ANY
            ]);
        },
        canManageOrganization: () => (organizationIds: string[]) => {
            return useEntitlementStore().hasClaim(
                Claims.Type.CAN_MANAGE_ORGANIZATION,
                [Claims.Value.ANY, ...organizationIds],
                [...organizationIds, Claims.Scope.UNDEFINED, Claims.Scope.ANY]
            );
        },
        canViewExerciseArea() {
            const scopes = useEntitlementStore().scopes;
            return useAuthenticationStore().isLogin && (this.canManageOrganization(scopes) || this.canListCourses(scopes));
        },
        canViewAllCompetitions: () => (organizationId: string) => {
            return useEntitlementStore().hasClaim(Claims.Type.CAN_VIEW_ALL_COMPETITIONS, [Claims.Value.ANY, organizationId], [
                organizationId,
                Claims.Scope.UNDEFINED,
                Claims.Scope.ANY
            ]);
        },
        canAccessCompetition(): (competitionId: string, organizationId: string) => boolean {
            return (competitionId: string, organizationId: string) => {
                //If the user has this ctf membership, good.
                if (useEntitlementStore().hasClaim(Claims.Type.CTF_COMPETITION, competitionId, [Claims.Scope.ANY, competitionId])) {
                    return true;
                }

                //If the user can view this organization's competitions, good. (e.g. courseadmin and superadmin)
                if (this.canViewAllCompetitions(organizationId)) {
                    return true;
                }

                return false;
            }
        },
        canRegisterAsAdmin: () => (courseId: string) => {
            return useEntitlementStore().hasClaim(Claims.Type.CAN_REGISTER_CTF_ADMIN, courseId, [
                courseId,
                Claims.Scope.UNDEFINED,
                Claims.Scope.ANY
            ]);
        },
        canRegisterAsPlayer: () => (courseId: string) => {
            return useEntitlementStore().hasClaim(Claims.Type.CAN_REGISTER_CTF_PLAYER, courseId, [
                courseId,
                Claims.Scope.UNDEFINED,
                Claims.Scope.ANY
            ]);
        },
        canCreateCourseApplication: () => (courseId: string, organizationId: string) => {
            return useEntitlementStore().hasClaim(
                Claims.Type.API,
                Claims.Value.POST_V1_COURSE_APPLICATION,
                [courseId, organizationId, Claims.Scope.UNDEFINED, Claims.Scope.ANY]
            );
        },
        canCreateSnapshotInOrganization: () => (organizationId: string) => {
            return useEntitlementStore().hasClaim(Claims.Type.CAN_CREATE_SNAPSHOT_IN_ORGANIZATION, [Claims.Value.ANY, organizationId], [
                organizationId,
                Claims.Scope.UNDEFINED,
                Claims.Scope.ANY
            ]);
        },
    }
});
